import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import {
  attachSpotlight,
  attachTilt,
  countUp,
  observeReveal,
} from '../lib/effects';

const FEATURES = [
  {
    icon: 'fa-solid fa-diagram-project',
    title: '공정표 자동 생성',
    desc: '계약 즉시 표준 공정표를 자동으로 생성하고, 현장별로 자유롭게 조정하세요.',
  },
  {
    icon: 'fa-solid fa-handshake',
    title: '하청 계약 · 기성',
    desc: '전자계약서, 기성금 청구, 보류금 관리까지 한 화면에서.',
  },
  {
    icon: 'fa-solid fa-coins',
    title: '매출 · 수금 트래킹',
    desc: '계약금, 중도금, 잔금까지 자동 분개. 미수금을 놓치지 마세요.',
  },
  {
    icon: 'fa-solid fa-receipt',
    title: '세금계산서 · 매입',
    desc: '발행/수취 일원화, 홈택스 연동으로 경리 업무를 자동화합니다.',
  },
  {
    icon: 'fa-solid fa-tools',
    title: 'A/S 접수 · 처리',
    desc: '카톡 알림 연동, 처리 이력 관리, 만족도 자동 수집.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '엔터프라이즈 보안',
    desc: 'HTTPS·HSTS, RBAC, 감사 로그, 테넌트 격리, 자동 백업.',
  },
];

const TRUST_BADGES = [
  { icon: 'fa-solid fa-lock', label: '은행급 보안' },
  { icon: 'fa-solid fa-shield-halved', label: '권한 분리' },
  { icon: 'fa-solid fa-clipboard-check', label: '전체 작업 기록' },
  { icon: 'fa-solid fa-cloud', label: '자동 백업' },
];

const PARTNERS = ['ABC건설', 'XYZ인테리어', '하이엔드디자인', '한라리모델링', '한솔홈스타일링'];

const STEPS = [
  {
    icon: 'fa-solid fa-user-plus',
    title: '1분 가입',
    desc: '회사명·이메일만으로 시작. 신용카드 없이 14일 무료.',
    detail: '메일 인증 즉시 owner 계정이 발급됩니다.',
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: '첫 현장 등록',
    desc: '계약 정보 입력 즉시 표준 공정표가 자동으로 생성됩니다.',
    detail: '10단계 표준 공정 + 착공일 기준 자동 일정 배분.',
  },
  {
    icon: 'fa-solid fa-chart-line',
    title: '한 화면에서 운영',
    desc: '공정·하청·매출·A/S 가 실시간으로 한 대시보드에 모입니다.',
    detail: '담당자별 RBAC + 모든 액션 audit log 기록.',
  },
];

const PROCESS_TEMPLATE = [
  { name: '철거', days: 3, color: 'bg-red-500' },
  { name: '설비', days: 5, color: 'bg-amber-500' },
  { name: '전기', days: 4, color: 'bg-yellow-500' },
  { name: '목공', days: 7, color: 'bg-emerald-500' },
  { name: '도장', days: 4, color: 'bg-teal-500' },
  { name: '타일', days: 5, color: 'bg-cyan-500' },
  { name: '바닥', days: 3, color: 'bg-sky-500' },
  { name: '도배', days: 3, color: 'bg-blue-500' },
  { name: '필름', days: 2, color: 'bg-indigo-500' },
  { name: '청소·준공', days: 2, color: 'bg-violet-500' },
];

const TESTIMONIALS = [
  {
    name: '김상민',
    role: '대표 · 하이엔드디자인',
    avatar: 'fa-solid fa-circle-user',
    quote:
      '엑셀 12개 파일을 동시에 띄워놓고 일하던 시절로 못 돌아갑니다. 매월 마감이 3일에서 1일로 줄었어요.',
  },
  {
    name: '박지영',
    role: '경리팀장 · 한라리모델링',
    avatar: 'fa-solid fa-circle-user',
    quote:
      '세금계산서 발행과 매출 분개가 자동이라 월말에 야근이 없어졌습니다. 하청 기성금 누락도 0건.',
  },
  {
    name: '이재호',
    role: '현장소장 · 한솔홈스타일링',
    avatar: 'fa-solid fa-circle-user',
    quote:
      '공정 하나 변경되면 카톡 단톡방 5개에 모두 공지하던 시절은 끝. 빌드업이 알아서 정리합니다.',
  },
];

const COMPARE = [
  { feature: '공정표 자동 생성', excel: false, buildup: true },
  { feature: '실시간 매출·미수금 집계', excel: false, buildup: true },
  { feature: '하청 기성금 자동 알림', excel: false, buildup: true },
  { feature: '세금계산서 발행/수취 일원화', excel: false, buildup: true },
  { feature: 'A/S 처리 이력 누적', excel: false, buildup: true },
  { feature: '권한별 데이터 접근 제어', excel: false, buildup: true },
  { feature: '모바일 현장 입력', excel: false, buildup: true },
  { feature: '월말 마감 시간', excel: '평균 3일', buildup: '평균 4시간' },
];

const FAQ = [
  {
    q: '기존에 쓰던 엑셀 데이터를 가져올 수 있나요?',
    a: '네, CSV 업로드 + 매핑 마법사로 현장·하청·매출 데이터를 한 번에 가져올 수 있습니다.',
  },
  {
    q: '인터넷이 끊긴 현장에서도 쓸 수 있나요?',
    a: '모바일 앱은 오프라인 입력을 지원하며 연결 복원 시 자동 동기화됩니다.',
  },
  {
    q: '하청업체도 사용해야 하나요?',
    a: '하청업체는 별도 가입 없이 SMS / 카톡 링크로 계약 확인과 기성 청구만 하면 됩니다.',
  },
  {
    q: '데이터는 어디에 저장되나요?',
    a: 'Cloudflare 글로벌 인프라에 저장되며 멀티테넌트 격리, 일일 백업, 90일 시점 복구가 적용됩니다.',
  },
];

export function Landing() {
  const heroRef = useRef<HTMLDivElement>(null);
  const counterRefs = useRef<HTMLSpanElement[]>([]);

  useEffect(() => {
    const off = observeReveal(document);
    return off;
  }, []);

  useEffect(() => {
    // 카드 spotlight + tilt
    const cards = document.querySelectorAll<HTMLElement>('[data-spotlight]');
    const offs: Array<() => void> = [];
    cards.forEach((c) => offs.push(attachSpotlight(c)));
    document
      .querySelectorAll<HTMLElement>('[data-tilt]')
      .forEach((c) => offs.push(attachTilt(c, 6)));
    return () => offs.forEach((f) => f());
  }, []);

  useEffect(() => {
    // 카운터 진입 시 시작
    const els = counterRefs.current.filter(Boolean);
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const target = parseInt((e.target as HTMLElement).dataset.to ?? '0', 10);
            countUp(e.target as HTMLElement, target, 1400);
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.5 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main id="main" className="relative z-[1]">
        {/* HERO */}
        <section
          ref={heroRef}
          className="pt-24 pb-16 sm:pt-28 sm:pb-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
        >
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-sm">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                <span>지금 1,247명의 현장소장이 빌드업을 사용 중</span>
              </div>
              <h1 className="reveal mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                인테리어 운영,
                <br />
                <span className="text-brand-600">엑셀 없이</span> 완성합니다.
              </h1>
              <p className="reveal mt-6 text-lg text-surface-900/80 dark:text-surface-100/80 max-w-xl">
                공정 · 하청 · 매출 · A/S 까지. 카카오톡과 수기 장부로 흩어진 운영을
                빌드업 한 곳에서 실시간으로 관리하세요.
              </p>
              <div className="reveal mt-8 flex flex-wrap gap-3">
                <Link to="/login" className="btn btn-primary">
                  <span>14일 무료 시작</span>
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
                <a href="#bento" className="btn btn-ghost">
                  <i className="fa-solid fa-circle-play" aria-hidden="true" />
                  <span>제품 데모 보기</span>
                </a>
              </div>

              {/* 신뢰 배지 */}
              <div id="trust" className="reveal mt-10 flex flex-wrap gap-2" aria-label="보안 인증">
                {TRUST_BADGES.map((b) => (
                  <span
                    key={b.label}
                    className="glass inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
                  >
                    <i className={`${b.icon} text-brand-600`} aria-hidden="true" />
                    {b.label}
                  </span>
                ))}
              </div>
            </div>

            {/* HERO 비주얼 — 글래스 카드 */}
            <div className="lg:col-span-5 reveal">
              <div
                data-tilt
                data-spotlight
                className="tilt-card spotlight glass material-concrete rounded-3xl p-6 aspect-[4/5] sm:aspect-square"
              >
                <div className="tilt-inner h-full flex flex-col">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">2026-04 매출</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                      <i className="fa-solid fa-arrow-trend-up mr-1" aria-hidden="true" />
                      +18.4%
                    </span>
                  </div>
                  <div className="mt-4 font-display text-4xl font-bold">
                    <span
                      ref={(el) => el && (counterRefs.current[0] = el)}
                      data-to="427000000"
                      aria-label="이번 달 매출"
                    >
                      0
                    </span>
                    <span className="ml-1 text-base font-medium">원</span>
                  </div>
                  <div className="mt-6 space-y-3">
                    {[
                      { name: '강남 아파트 리모델링', pct: 72 },
                      { name: '판교 카페 신축', pct: 35 },
                      { name: '용산 사무실 부분공사', pct: 90 },
                    ].map((p) => (
                      <div key={p.name}>
                        <div className="flex justify-between text-sm">
                          <span>{p.name}</span>
                          <span className="text-surface-900/60 dark:text-surface-100/60">
                            {p.pct}%
                          </span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-surface-200/70 dark:bg-white/10 overflow-hidden">
                          <div
                            className="h-full bg-brand-600 rounded-full transition-[width] duration-700"
                            style={{ width: `${p.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-6">
                    <div className="flex -space-x-2">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-8 w-8 rounded-full bg-gradient-to-br from-brand-400 to-accent-500 ring-2 ring-white dark:ring-surface-950"
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                    <span className="text-xs text-surface-900/60 dark:text-surface-100/60">
                      현장 12개 활성
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PARTNER LOGOS */}
        <section className="py-10 border-y border-surface-200 dark:border-white/10 bg-white/40 dark:bg-surface-950/40">
          <div className="mx-auto max-w-7xl px-6">
            <p className="text-center text-xs uppercase tracking-widest text-surface-900/60 dark:text-surface-100/60">
              전국 인테리어 업체가 빌드업을 신뢰합니다
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-x-10 gap-y-4 opacity-80">
              {PARTNERS.map((p) => (
                <span key={p} className="font-display font-semibold text-lg">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-24 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">3단계로 시작</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              가입부터 첫 운영까지, 단 5분.
            </h2>
            <p className="mt-3 text-surface-900/70 dark:text-surface-100/70">
              복잡한 셋업 없이, 그대로 운영을 시작할 수 있도록 설계했습니다.
            </p>
          </div>
          <ol className="mt-12 grid gap-6 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <li
                key={s.title}
                data-spotlight
                className="reveal spotlight glass rounded-3xl p-6 relative"
              >
                <span
                  className="absolute right-6 top-6 font-display text-6xl font-extrabold text-brand-600/10 dark:text-brand-400/10"
                  aria-hidden="true"
                >
                  0{i + 1}
                </span>
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-600 text-white">
                  <i className={`${s.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-display text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-surface-900/70 dark:text-surface-100/70">
                  {s.desc}
                </p>
                <p className="mt-3 text-xs text-surface-900/60 dark:text-surface-100/60">
                  <i className="fa-solid fa-circle-info mr-1" aria-hidden="true" />
                  {s.detail}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* GANTT 미리보기 */}
        <section className="py-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">표준 공정 템플릿</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              계약 즉시, 38일치 공정표가.
            </h2>
            <p className="mt-3 text-surface-900/70 dark:text-surface-100/70">
              현장 등록 시 자동으로 배치됩니다. 일정과 소요일은 자유롭게 조정 가능.
            </p>
          </div>
          <div className="reveal mt-10 glass rounded-3xl p-6 overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[120px_1fr] gap-3 mb-4 text-xs font-mono text-surface-900/60 dark:text-surface-100/60">
                <div>공정</div>
                <div className="grid grid-cols-10 gap-1">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="text-center">
                      D+{i * 4}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2.5">
                {PROCESS_TEMPLATE.map((p, i) => {
                  const start = PROCESS_TEMPLATE.slice(0, i).reduce(
                    (acc, x) => acc + x.days,
                    0,
                  );
                  const total = 38;
                  return (
                    <div
                      key={p.name}
                      className="grid grid-cols-[120px_1fr] gap-3 items-center"
                    >
                      <div className="text-sm">
                        <i className="fa-solid fa-grip-vertical mr-2 text-surface-900/30 dark:text-surface-100/30" aria-hidden="true" />
                        {p.name}
                      </div>
                      <div className="relative h-7 rounded-lg bg-surface-200/40 dark:bg-white/5">
                        <div
                          className={`${p.color} absolute h-full rounded-lg flex items-center justify-end px-2 text-[10px] text-white font-semibold shadow-md`}
                          style={{
                            left: `${(start / total) * 100}%`,
                            width: `${(p.days / total) * 100}%`,
                          }}
                          aria-label={`${p.name} ${p.days}일`}
                        >
                          {p.days}일
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 pt-4 border-t border-surface-200/60 dark:border-white/10 flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-surface-900/60 dark:text-surface-100/60">
                  <i className="fa-solid fa-circle-info" aria-hidden="true" />
                  드래그로 일정 조정 · 더블클릭으로 공정 추가
                </span>
                <span className="font-mono">총 38일 / 10단계</span>
              </div>
            </div>
          </div>
        </section>

        {/* BENTO 그리드 */}
        <section id="bento" className="py-24 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">대시보드</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              필요한 정보를, 필요한 자리에.
            </h2>
            <p className="mt-3 text-surface-900/70 dark:text-surface-100/70">
              CSS Grid 기반 Bento 레이아웃으로, 한 화면에서 운영 전체가 보입니다.
            </p>
          </div>

          <div className="mt-10 bento">
            {/* 가로로 큰 카드 */}
            <div
              data-spotlight
              className="reveal spotlight glass lg:col-span-4 rounded-3xl p-6 min-h-[260px] flex flex-col justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-600 text-white">
                  <i className="fa-solid fa-chart-line" aria-hidden="true" />
                </span>
                <h3 className="font-display text-xl font-bold">실시간 매출 트렌드</h3>
              </div>
              <div className="grid grid-cols-7 gap-2 items-end h-32">
                {[35, 50, 42, 70, 58, 85, 92].map((h, i) => (
                  <div
                    key={i}
                    className="bg-gradient-to-t from-brand-500 to-accent-500 rounded-md"
                    style={{ height: `${h}%` }}
                    aria-label={`${i + 1}월: ${h}%`}
                  />
                ))}
              </div>
            </div>
            {/* 세로 작은 카드들 */}
            <div data-spotlight className="reveal spotlight glass lg:col-span-2 rounded-3xl p-6">
              <i className="fa-solid fa-people-group text-2xl text-brand-600" aria-hidden="true" />
              <p className="mt-2 text-sm">하청 활성</p>
              <div className="mt-3 font-display text-3xl font-bold">
                <span
                  ref={(el) => el && (counterRefs.current[1] = el)}
                  data-to="48"
                  aria-label="활성 하청 수"
                >
                  0
                </span>
              </div>
              <p className="text-xs mt-1 text-emerald-600">+6 이번 주</p>
            </div>

            <div data-spotlight className="reveal spotlight glass lg:col-span-3 rounded-3xl p-6">
              <i className="fa-solid fa-calendar-days text-2xl text-brand-600" aria-hidden="true" />
              <p className="mt-2 text-sm">진행 중 공정</p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex justify-between">
                  <span><i className="fa-solid fa-paint-roller mr-2" aria-hidden="true" />도장</span>
                  <span className="text-surface-900/60 dark:text-surface-100/60">강남 / 5일차</span>
                </li>
                <li className="flex justify-between">
                  <span><i className="fa-solid fa-bolt mr-2" aria-hidden="true" />전기</span>
                  <span className="text-surface-900/60 dark:text-surface-100/60">판교 / 2일차</span>
                </li>
                <li className="flex justify-between">
                  <span><i className="fa-solid fa-hammer mr-2" aria-hidden="true" />목공</span>
                  <span className="text-surface-900/60 dark:text-surface-100/60">용산 / 3일차</span>
                </li>
              </ul>
            </div>

            <div data-spotlight className="reveal spotlight glass lg:col-span-3 rounded-3xl p-6">
              <i className="fa-solid fa-clipboard-list text-2xl text-brand-600" aria-hidden="true" />
              <p className="mt-2 text-sm">A/S 처리율</p>
              <div className="mt-3 font-display text-3xl font-bold">
                <span
                  ref={(el) => el && (counterRefs.current[2] = el)}
                  data-to="97"
                  aria-label="A/S 처리율 퍼센트"
                >
                  0
                </span>
                %
              </div>
              {/* skeleton 예시 */}
              <div className="mt-4 space-y-2" aria-hidden="true">
                <div className="skeleton h-3 w-full" />
                <div className="skeleton h-3 w-4/5" />
                <div className="skeleton h-3 w-3/5" />
              </div>
            </div>
          </div>

          {/* 카드 가로 스크롤 (모바일) */}
          <div
            className="mt-6 -mx-4 px-4 sm:hidden flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
            aria-label="모바일 빠른 통계 (가로 스크롤)"
          >
            {[
              { label: '오늘 수금', value: '5,200,000원' },
              { label: '미수금', value: '12,800,000원' },
              { label: '진행 현장', value: '12' },
              { label: 'A/S 대기', value: '3' },
            ].map((s) => (
              <div
                key={s.label}
                className="snap-center min-w-[70%] glass rounded-2xl p-4"
              >
                <p className="text-xs">{s.label}</p>
                <p className="mt-2 font-display text-xl font-bold">{s.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 도면 디바이더 — 인테리어 도메인 시각 코드 */}
        <div className="schematic-divider mx-auto" aria-hidden="true" />

        {/* FEATURES */}
        <section id="features" className="py-24 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">기능</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              현장에 필요한 모든 것.
            </h2>
          </div>
          {/* asymmetric Bento — 3x2 균일 grid 의 AI slop 패턴을 깨고 첫 카드를 hero 로 */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:auto-rows-[1fr]">
            {FEATURES.map((f, i) => {
              const isHero = i === 0;
              return (
                <article
                  key={f.title}
                  data-tilt
                  data-spotlight
                  className={[
                    'reveal tilt-card spotlight glass rounded-3xl p-6 flex flex-col',
                    isHero
                      ? 'sm:col-span-2 lg:col-span-2 lg:row-span-2 lg:p-10 material-concrete bg-gradient-to-br from-brand-600/[0.08] to-accent-500/[0.06]'
                      : '',
                  ].join(' ')}
                >
                  <div className="tilt-inner flex-1 flex flex-col">
                    <span
                      className={[
                        'grid place-items-center rounded-xl bg-brand-600/10 text-brand-600 ring-1 ring-brand-600/20',
                        isHero ? 'h-16 w-16' : 'h-12 w-12',
                      ].join(' ')}
                    >
                      <i
                        className={`${f.icon} ${isHero ? 'text-3xl' : 'text-xl'}`}
                        aria-hidden="true"
                      />
                    </span>
                    <h3 className="mt-4 font-display font-bold text-xl">
                      {f.title}
                    </h3>
                    <p
                      className={[
                        'mt-2 text-surface-900/70 dark:text-surface-100/70',
                        isHero ? 'text-base sm:text-lg max-w-md' : 'text-sm',
                      ].join(' ')}
                    >
                      {f.desc}
                    </p>
                    {isHero && (
                      <div className="mt-auto pt-6 grid grid-cols-3 gap-3 text-xs">
                        {[
                          { k: '템플릿 단계', v: '10' },
                          { k: '평균 셋업 시간', v: '38일' },
                          { k: '재배치 클릭', v: '1' },
                        ].map((m) => (
                          <div
                            key={m.k}
                            className="rounded-xl bg-white/40 dark:bg-white/[0.03] border border-white/40 dark:border-white/10 p-3"
                          >
                            <div className="font-display text-2xl font-extrabold text-brand-600">
                              {m.v}
                            </div>
                            <div className="text-surface-900/60 dark:text-surface-100/60 mt-1">
                              {m.k}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-24 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">고객의 목소리</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              매일 쓰는 분들이 말합니다.
            </h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6 lg:items-center">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={t.name}
                data-spotlight
                className={[
                  'reveal spotlight glass rounded-3xl flex flex-col',
                  i === 1
                    ? 'p-8 lg:py-10 ring-2 ring-brand-600/20 lg:scale-[1.03]'
                    : 'p-6',
                ].join(' ')}
              >
                <div className="text-accent-500" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="fa-solid fa-star mr-0.5" />
                  ))}
                </div>
                <blockquote className="mt-4 text-base flex-1">
                  <i
                    className="fa-solid fa-quote-left text-brand-600/30 mr-2"
                    aria-hidden="true"
                  />
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3 pt-4 border-t border-surface-200/60 dark:border-white/10">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-gradient-to-br from-brand-400 to-accent-500 text-white">
                    <i className={t.avatar} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block font-semibold">{t.name}</span>
                    <span className="block text-xs text-surface-900/60 dark:text-surface-100/60">
                      {t.role}
                    </span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* COMPARE */}
        <section className="py-24 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">엑셀 vs 빌드업</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              왜 빌드업으로 옮길까요?
            </h2>
          </div>
          <div className="reveal mt-10 glass rounded-3xl p-2 sm:p-4 overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="text-sm">
                  <th className="text-left font-semibold py-4 px-3 sm:px-4">기능</th>
                  <th className="text-center py-4 px-3 sm:px-4 text-surface-900/60 dark:text-surface-100/60">
                    <i className="fa-solid fa-file-excel mr-1" aria-hidden="true" />
                    엑셀 + 카톡
                  </th>
                  <th className="text-center py-4 px-3 sm:px-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-600 text-white text-xs">
                      <i className="fa-solid fa-cube" aria-hidden="true" /> 빌드업
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, idx) => (
                  <tr
                    key={row.feature}
                    className={
                      idx % 2 === 0
                        ? 'bg-white/30 dark:bg-white/[0.02]'
                        : ''
                    }
                  >
                    <td className="py-3 px-3 sm:px-4 text-sm">{row.feature}</td>
                    <td className="text-center py-3 px-3 sm:px-4 text-sm text-surface-900/60 dark:text-surface-100/60">
                      {typeof row.excel === 'boolean' ? (
                        row.excel ? (
                          <i className="fa-solid fa-circle-check text-emerald-500" aria-hidden="true" />
                        ) : (
                          <i className="fa-solid fa-xmark text-surface-900/40 dark:text-surface-100/40" aria-hidden="true" />
                        )
                      ) : (
                        row.excel
                      )}
                    </td>
                    <td className="text-center py-3 px-3 sm:px-4 text-sm font-semibold text-brand-600">
                      {typeof row.buildup === 'boolean' ? (
                        row.buildup ? (
                          <i className="fa-solid fa-circle-check" aria-hidden="true" />
                        ) : (
                          <i className="fa-solid fa-xmark" aria-hidden="true" />
                        )
                      ) : (
                        row.buildup
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="reveal max-w-2xl">
            <p className="text-brand-600 font-semibold">자주 묻는 질문</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">
              결정 전 마지막 5가지.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {FAQ.map((f) => (
              <details
                key={f.q}
                className="reveal glass rounded-2xl p-5 group"
              >
                <summary className="cursor-pointer font-semibold flex items-center gap-3 list-none">
                  <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand-600/10 text-brand-600 shrink-0">
                    <i
                      className="fa-solid fa-plus group-open:hidden"
                      aria-hidden="true"
                    />
                    <i
                      className="fa-solid fa-minus hidden group-open:inline"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="flex-1">{f.q}</span>
                </summary>
                <p className="mt-3 ml-11 text-sm text-surface-900/80 dark:text-surface-100/80">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
          <div className="reveal mt-8 text-center text-sm">
            더 많은 질문은{' '}
            <Link to="/help" className="text-brand-600 hover:underline">
              도움말 페이지
            </Link>
            에서 확인하세요.
          </div>
        </section>

        {/* CTA */}
        <section id="pricing" className="py-24 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div
            data-spotlight
            className="reveal spotlight glass rounded-3xl p-10 text-center"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              지금 바로 14일 무료로 시작하세요.
            </h2>
            <p className="mt-3 text-surface-900/70 dark:text-surface-100/70">
              신용카드 없이 현장 1개부터 자유롭게.
            </p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              {/* Hero CTA 와 동일 패턴: Link 자체가 button 스타일. <button><a> 중첩은
                  invalid HTML 이고 click 영역과 navigation 이 어긋난다. */}
              <Link to="/login" className="btn btn-primary">
                <i className="fa-solid fa-rocket" aria-hidden="true" />
                <span>무료로 시작</span>
              </Link>
              <Link to="/help" className="btn btn-ghost">
                <i className="fa-solid fa-headset" aria-hidden="true" />
                <span>도입 상담</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
